import type {
  Workspace,
  WorkspaceWithMemberCount,
  WorkspaceMemberWithUser,
  CreateWorkspaceDto,
  UpdateWorkspaceDto,
  UpdateMemberRoleDto,
  MemberRole,
  MemberStatus,
} from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Schemas
const WorkspaceBaseSchema = z.object({
  id: z.number(),
  name: z.string(),
  thumbnailUrl: z.string().nullish(),
  bannerUrl: z.string().nullish(),
  creatorId: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const WorkspaceSchema: z.ZodType<Workspace> = WorkspaceBaseSchema;

const WorkspaceWithMemberCountSchema: z.ZodType<WorkspaceWithMemberCount> =
  WorkspaceBaseSchema.extend({
    memberCount: z.number(),
  });

const WorkspaceMemberWithUserSchema: z.ZodType<WorkspaceMemberWithUser> =
  z.object({
    id: z.number(),
    workspaceId: z.number(),
    userId: z.number(),
    role: z.nativeEnum({
      MASTER: "master",
      MEMBER: "member",
    } as const) as z.ZodType<MemberRole>,
    status: z.nativeEnum({
      PENDING: "pending",
      ACCEPTED: "accepted",
      REJECTED: "rejected",
      EXPELLED: "expelled",
    } as const) as z.ZodType<MemberStatus>,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    user: z.object({
      id: z.number(),
      email: z.string(),
      nickname: z.string().nullish(),
      avatarUrl: z.string().nullish(),
    }),
  });

// Response Schemas
const CreateWorkspaceResponseSchema: z.ZodType<
  DataAndMessageResponse<Workspace>
> = z.object({
  data: WorkspaceSchema,
  message: z.string(),
});

const GetWorkspacesResponseSchema: z.ZodType<
  DataAndMessageResponse<WorkspaceWithMemberCount[]>
> = z.object({
  data: z.array(WorkspaceWithMemberCountSchema),
  message: z.string(),
});

const GetWorkspaceResponseSchema: z.ZodType<
  DataAndMessageResponse<WorkspaceWithMemberCount>
> = z.object({
  data: WorkspaceWithMemberCountSchema,
  message: z.string(),
});

const UpdateWorkspaceResponseSchema: z.ZodType<
  DataAndMessageResponse<Workspace>
> = z.object({
  data: WorkspaceSchema,
  message: z.string(),
});

const DeleteWorkspaceResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const GetMembersResponseSchema: z.ZodType<
  DataAndMessageResponse<WorkspaceMemberWithUser[]>
> = z.object({
  data: z.array(WorkspaceMemberWithUserSchema),
  message: z.string(),
});

const UpdateMemberRoleResponseSchema: z.ZodType<
  DataAndMessageResponse<WorkspaceMemberWithUser>
> = z.object({
  data: WorkspaceMemberWithUserSchema,
  message: z.string(),
});

const ExpelMemberResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const GetLastVisitResponseSchema: z.ZodType<
  DataAndMessageResponse<{ workspaceId: number | null }>
> = z.object({
  data: z.object({
    workspaceId: z.number().nullable(),
  }),
  message: z.string(),
});

export function createWorkspaceModule(httpClient: HttpClient) {
  return {
    create: async (data: CreateWorkspaceDto) => {
      const response = await httpClient.post<
        typeof CreateWorkspaceResponseSchema
      >("/api/v1/workspaces", {
        data,
        schema: CreateWorkspaceResponseSchema,
      });
      return response.data;
    },

    list: async () => {
      const response = await httpClient.get<typeof GetWorkspacesResponseSchema>(
        "/api/v1/workspaces",
        {
          schema: GetWorkspacesResponseSchema,
        },
      );
      return response.data;
    },

    get: async (workspaceId: number) => {
      const response = await httpClient.get<typeof GetWorkspaceResponseSchema>(
        `/api/v1/workspaces/${workspaceId}`,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          schema: GetWorkspaceResponseSchema,
        },
      );
      return response.data;
    },

    update: async (workspaceId: number, data: UpdateWorkspaceDto) => {
      const response = await httpClient.patch<
        typeof UpdateWorkspaceResponseSchema
      >(`/api/v1/workspaces/${workspaceId}`, {
        data,
        schema: UpdateWorkspaceResponseSchema,
      });
      return response.data;
    },

    remove: async (workspaceId: number) => {
      const response = await httpClient.delete<
        typeof DeleteWorkspaceResponseSchema
      >(`/api/v1/workspaces/${workspaceId}`, {
        schema: DeleteWorkspaceResponseSchema,
      });
      return response;
    },

    getLastVisit: async () => {
      const response = await httpClient.get<typeof GetLastVisitResponseSchema>(
        "/api/v1/workspaces/last-visit",
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          schema: GetLastVisitResponseSchema,
        },
      );
      return response.data;
    },

    getMembers: async (workspaceId: number) => {
      const response = await httpClient.get<typeof GetMembersResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/members`,
        {
          schema: GetMembersResponseSchema,
        },
      );
      return response.data;
    },

    updateMemberRole: async (
      workspaceId: number,
      userId: number,
      data: UpdateMemberRoleDto,
    ) => {
      const response = await httpClient.patch<
        typeof UpdateMemberRoleResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/members/${userId}`, {
        data,
        schema: UpdateMemberRoleResponseSchema,
      });
      return response.data;
    },

    expelMember: async (workspaceId: number, userId: number) => {
      const response = await httpClient.delete<
        typeof ExpelMemberResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/members/${userId}`, {
        schema: ExpelMemberResponseSchema,
      });
      return response;
    },
  };
}
